import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  MessageCircle,
  Plus,
  Send,
  UserCheck,
  UserMinus,
} from 'lucide-react';
import api from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import { connectSocket } from '../../lib/socket';
import { resolveMediaUrl } from '../../lib/mediaUrl';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Textarea from '../../components/ui/Textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';

type ThreadRow = {
  id: string;
  waPhoneE164: string;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  unreadCountAgency: number;
  assignedUserId: string | null;
  kolProfile?: { id: string; name: string; profileImageUrl?: string | null } | null;
  job?: { id: string; title: string } | null;
  assignedUser?: { id: string; fullName: string | null; email: string } | null;
};

type InboxMessage = {
  id: string;
  direction: 'INBOUND' | 'OUTBOUND';
  body: string | null;
  messageType: string;
  templateName?: string | null;
  createdAt: string;
  deliveryStatus?: string | null;
  sentByUser?: { id: string; fullName: string | null; email: string } | null;
};

type QuickReply = {
  id: string;
  title: string;
  body: string;
};

type WindowStatus = {
  freeTextAllowed: boolean;
  expiresAt: string | null;
};

const INBOX_UNREAD_UPDATE_EVENT = 'agency-inbox-unread-update';

function formatTs(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatThreadTs(iso: string | null | undefined): string {
  if (!iso) return '';
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) return iso;
  const today = new Date();
  const startToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startValue = new Date(value.getFullYear(), value.getMonth(), value.getDate());
  const dayDifference = Math.round((startToday.getTime() - startValue.getTime()) / 86_400_000);
  if (dayDifference === 0) {
    return value.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (dayDifference === 1) return 'Yest.';
  return value.toLocaleDateString([], { day: 'numeric', month: 'short' });
}

function messageDay(iso: string): string {
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) return '';
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (value.toDateString() === today.toDateString()) return 'Today';
  if (value.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return value.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

function messageTime(iso: string): string {
  const value = new Date(iso);
  if (Number.isNaN(value.getTime())) return iso;
  return value.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function threadLabel(t: ThreadRow): string {
  return t.kolProfile?.name || t.waPhoneE164;
}

type AgencyOrgOption = {
  id: string;
  name: string;
  roleInOrg?: 'OWNER' | 'ADMIN' | 'MANAGER' | 'STAFF' | 'VIEWER';
};

export default function AgencyWhatsAppInbox() {
  const { user } = useAuthStore();
  const agencyOrgs = useMemo<AgencyOrgOption[]>(
    () =>
      (user?.memberships || [])
        .filter((m) => m.organization?.type === 'AGENCY')
        .map((m) => ({
          id: m.organizationId,
          name: m.organization?.name || m.organizationId,
          roleInOrg: m.roleInOrg as AgencyOrgOption['roleInOrg'],
        })),
    [user?.memberships]
  );

  const [organizationId, setOrganizationId] = useState('');
  const [threads, setThreads] = useState<ThreadRow[]>([]);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [windowStatus, setWindowStatus] = useState<WindowStatus>({
    freeTextAllowed: false,
    expiresAt: null,
  });
  const [readOnly, setReadOnly] = useState(false);
  const [quickReplies, setQuickReplies] = useState<QuickReply[]>([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState('');
  const [composer, setComposer] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState<'all' | 'mine' | 'unassigned'>('all');
  const [newPhone, setNewPhone] = useState('');
  const [newConversationOpen, setNewConversationOpen] = useState(false);
  const [newConversationError, setNewConversationError] = useState('');
  const [startingConversation, setStartingConversation] = useState(false);
  const [_showQuickReplyForm, _setShowQuickReplyForm] = useState(false);
  const [_newQuickTitle, _setNewQuickTitle] = useState('');
  const [_newQuickBody, _setNewQuickBody] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  const selectedThread = threads.find((t) => t.id === selectedThreadId) ?? null;
  const orgReadOnly =
    readOnly ||
    agencyOrgs.find((o) => o.id === organizationId)?.roleInOrg === 'OWNER' ||
    agencyOrgs.find((o) => o.id === organizationId)?.roleInOrg === 'VIEWER';

  useEffect(() => {
    if (!organizationId && agencyOrgs.length > 0) {
      setOrganizationId(agencyOrgs[0].id);
    }
  }, [agencyOrgs, organizationId]);

  const loadThreads = useCallback(async () => {
    if (!organizationId) return;
    setLoadingThreads(true);
    setError('');
    try {
      const params: Record<string, string> = { organizationId };
      if (filter === 'mine' && user?.id) params.assignedUserId = user.id;
      if (filter === 'unassigned') params.unassignedOnly = 'true';
      const res = await api.get<{ threads: ThreadRow[] }>('/whatsapp-inbox/threads', { params });
      setThreads(res.data.threads || []);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to load conversations';
      setError(msg);
    } finally {
      setLoadingThreads(false);
    }
  }, [organizationId, filter, user?.id]);

  const loadQuickReplies = useCallback(async () => {
    if (!organizationId) return;
    try {
      const res = await api.get<QuickReply[]>('/whatsapp-inbox/quick-replies', {
        params: { organizationId },
      });
      setQuickReplies(res.data || []);
    } catch {
      /* optional */
    }
  }, [organizationId]);

  const loadThreadDetail = useCallback(async (threadId: string) => {
    setLoadingMessages(true);
    setError('');
    try {
      const res = await api.get<{
        thread: ThreadRow;
        messages: InboxMessage[];
        window: WindowStatus;
        readOnly: boolean;
      }>(`/whatsapp-inbox/threads/${threadId}`);
      setMessages(res.data.messages || []);
      setWindowStatus(res.data.window);
      setReadOnly(res.data.readOnly);
      window.dispatchEvent(
        new CustomEvent(INBOX_UNREAD_UPDATE_EVENT, {
          detail: { threadId, unreadCount: 0 },
        })
      );
      setThreads((prev) => {
        const t = res.data.thread;
        const idx = prev.findIndex((x) => x.id === t.id);
        if (idx < 0) return [t, ...prev];
        const next = [...prev];
        next[idx] = { ...next[idx], ...t, unreadCountAgency: 0 };
        return next;
      });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to load thread';
      setError(msg);
    } finally {
      setLoadingMessages(false);
    }
  }, []);

  useEffect(() => {
    loadThreads();
    loadQuickReplies();
  }, [loadThreads, loadQuickReplies]);

  useEffect(() => {
    if (selectedThreadId) loadThreadDetail(selectedThreadId);
    else {
      setMessages([]);
      setWindowStatus({ freeTextAllowed: false, expiresAt: null });
    }
  }, [selectedThreadId, loadThreadDetail]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!organizationId) return;
    let mounted = true;
    let activeSocket: Awaited<ReturnType<typeof connectSocket>> = null;
    const onMessage = (payload: { threadId: string; message: InboxMessage }) => {
      if (payload.threadId === selectedThreadId) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === payload.message.id)) return prev;
          return [...prev, payload.message];
        });
        loadThreadDetail(payload.threadId);
      }
      loadThreads();
    };
    const onThreadUpdate = () => loadThreads();
    const onAssigned = (payload: { thread?: ThreadRow }) => {
      if (payload.thread) {
        setThreads((prev) =>
          prev.map((t) => (t.id === payload.thread!.id ? { ...t, ...payload.thread! } : t))
        );
      }
    };

    void connectSocket().then((socket) => {
      if (!mounted || !socket) return;
      activeSocket = socket;
      socket.emit('join-org', organizationId);
      socket.on('whatsapp:message:new', onMessage);
      socket.on('whatsapp:thread:updated', onThreadUpdate);
      socket.on('whatsapp:thread:assigned', onAssigned);
    });
    return () => {
      mounted = false;
      const socket = activeSocket;
      socket?.off('whatsapp:message:new', onMessage);
      socket?.off('whatsapp:thread:updated', onThreadUpdate);
      socket?.off('whatsapp:thread:assigned', onAssigned);
      socket?.emit('leave-org', organizationId);
    };
  }, [organizationId, selectedThreadId, loadThreads, loadThreadDetail]);

  const handleSend = async () => {
    if (!selectedThreadId || orgReadOnly) return;
    const text = composer.trim();
    const tpl = templateName.trim();
    if (!text && !tpl) return;
    setSending(true);
    setError('');
    try {
      const body: Record<string, unknown> = {};
      if (tpl) body.templateName = tpl;
      else body.text = text;
      const res = await api.post<{ message: InboxMessage; window: WindowStatus }>(
        `/whatsapp-inbox/threads/${selectedThreadId}/messages`,
        body
      );
      setMessages((prev) => [...prev, res.data.message]);
      setWindowStatus(res.data.window);
      setComposer('');
      setTemplateName('');
      loadThreads();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to send message';
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  const handleAssign = async (force = false) => {
    if (!selectedThreadId) return;
    try {
      await api.post(`/whatsapp-inbox/threads/${selectedThreadId}/assign`, { force });
      loadThreadDetail(selectedThreadId);
      loadThreads();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to assign thread';
      setError(msg);
    }
  };

  const handleRelease = async () => {
    if (!selectedThreadId) return;
    try {
      await api.post(`/whatsapp-inbox/threads/${selectedThreadId}/release`);
      loadThreadDetail(selectedThreadId);
      loadThreads();
    } catch (e: unknown) {
      setError('Failed to release thread');
    }
  };

  const handleStartThread = async (surface: 'inline' | 'sheet') => {
    if (!organizationId || !newPhone.trim()) return;
    setError('');
    setNewConversationError('');
    setStartingConversation(true);
    try {
      const res = await api.post<{ thread: ThreadRow }>('/whatsapp-inbox/threads/start', {
        organizationId,
        to: newPhone.trim(),
      });
      setThreads((prev) => {
        if (prev.some((t) => t.id === res.data.thread.id)) return prev;
        return [res.data.thread, ...prev];
      });
      setSelectedThreadId(res.data.thread.id);
      setNewPhone('');
      setNewConversationOpen(false);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Could not start conversation';
      if (surface === 'sheet') setNewConversationError(msg);
      else setError(msg);
    } finally {
      setStartingConversation(false);
    }
  };

  const composerDisabled =
    orgReadOnly ||
    (windowStatus.freeTextAllowed ? !composer.trim() : !templateName.trim());

  return (
    <div className="ph-no-capture mx-auto max-w-[1400px] space-y-3 md:space-y-4 md:p-6">
      <div
        className={`${selectedThread ? 'hidden lg:flex' : 'flex'} flex-col gap-3 md:flex-row md:items-center md:justify-between`}
      >
        <div>
          <h1 className="page-title flex items-center gap-2 text-[22px] md:text-2xl md:font-semibold">
            <MessageCircle className="text-accentText" size={24} />
            WhatsApp inbox
          </h1>
          <p className="mt-1 hidden text-sm text-glass-text-2 md:block">
            Shared team inbox for post-interest KOL conversations. Configure WABA under{' '}
            <Link to="/agency/messaging" className="text-accentText hover:underline">
              WhatsApp &amp; messaging
            </Link>
            .
          </p>
        </div>
        {agencyOrgs.length > 1 && (
          <Select
            value={organizationId}
            onChange={(e) => {
              setOrganizationId(e.target.value);
              setSelectedThreadId(null);
            }}
            className="hidden max-w-xs lg:block"
          >
            {agencyOrgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </Select>
        )}
      </div>

      {error && !selectedThread && (
        <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      {orgReadOnly && !selectedThread && (
        <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
          Read-only mode — owners and viewers can read all threads but cannot send messages.
        </div>
      )}

      <div className="grid min-h-[70vh] grid-cols-1 gap-3 lg:grid-cols-[320px_minmax(0,1fr)] lg:gap-4">
        <Card
          className={`${selectedThread ? 'hidden lg:flex' : 'flex'} min-h-0 flex-col overflow-hidden lg:flex`}
        >
          <CardHeader className="space-y-3 p-3 md:p-6 md:pb-2">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="text-[15px] font-bold">Conversations</CardTitle>
                {agencyOrgs.length > 0 ? (
                  <p className="mt-1 truncate text-[11px] text-glass-text-3 md:hidden">
                    {agencyOrgs.find((org) => org.id === organizationId)?.name || 'Agency inbox'}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => {
                  setNewConversationError('');
                  setNewConversationOpen(true);
                }}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent2 text-white shadow-accent-glow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/60 lg:hidden"
                aria-label="Start a new conversation"
              >
                <Plus className="h-5 w-5" aria-hidden />
              </button>
            </div>
            {agencyOrgs.length > 1 && (
              <Select
                value={organizationId}
                onChange={(event) => {
                  setOrganizationId(event.target.value);
                  setSelectedThreadId(null);
                }}
                className="h-11 text-sm lg:hidden"
                aria-label="Agency organization"
              >
                {agencyOrgs.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </Select>
            )}
            <div className="glass-scroll -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
              {(['all', 'mine', 'unassigned'] as const).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={`min-h-11 shrink-0 touch-manipulation rounded-full border px-3 text-xs font-semibold transition-colors lg:min-h-0 lg:py-1 ${
                    filter === f
                      ? 'border-accent2/40 bg-accent2/20 text-accentText'
                      : 'border-glass text-glass-text-2 hover:bg-glass-2'
                  }`}
                >
                  {f === 'all' ? 'All' : f === 'mine' ? 'Mine' : 'Unassigned'}
                </button>
              ))}
            </div>
            <div className="hidden gap-2 lg:flex">
              <Input
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                placeholder="+60… new number"
                className="text-sm"
              />
              <Button
                data-help-id="whatsapp.new_conversation_button"
                type="button"
                variant="secondary"
                onClick={() => void handleStartThread('inline')}
                disabled={!newPhone.trim() || startingConversation}
              >
                {startingConversation ? 'Starting…' : 'New'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="flex-1 divide-y divide-glass overflow-y-auto p-0">
            {loadingThreads && <p className="p-4 text-sm text-glass-text-2">Loading…</p>}
            {!loadingThreads && threads.length === 0 && (
              <p className="p-4 text-sm text-glass-text-2">No conversations yet.</p>
            )}
            {threads.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setSelectedThreadId(t.id)}
                className={`min-h-[76px] w-full touch-manipulation px-3 py-2.5 text-left transition-colors hover:bg-glass-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent2/50 md:px-4 md:py-3 ${
                  selectedThreadId === t.id ? 'bg-glass-3' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  {t.kolProfile?.profileImageUrl ? (
                    <img
                      src={resolveMediaUrl(t.kolProfile.profileImageUrl)}
                      alt=""
                      className="h-10 w-10 shrink-0 rounded-full border border-glass object-cover"
                    />
                  ) : (
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent2/15 text-sm font-bold text-accentText">
                      {threadLabel(t).slice(0, 1).toUpperCase()}
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-glass-text">
                        {threadLabel(t)}
                      </span>
                      <span className="shrink-0 text-[11px] text-glass-text-3">
                        {formatThreadTs(t.lastMessageAt)}
                      </span>
                      {t.unreadCountAgency > 0 && (
                        <Badge className="shrink-0">{t.unreadCountAgency}</Badge>
                      )}
                    </span>
                    {t.job?.title && (
                      <span className="mt-0.5 block truncate text-xs font-medium text-accentText">
                        {t.job.title}
                      </span>
                    )}
                    <span className="mt-0.5 block truncate text-xs text-glass-text-2">
                      {t.lastMessagePreview || '—'}
                    </span>
                    <span className="mt-0.5 block truncate text-[11px] text-glass-text-3">
                      {t.assignedUser?.fullName || t.assignedUser?.email || 'Unassigned'}
                    </span>
                  </span>
                </div>
              </button>
            ))}
          </CardContent>
        </Card>

        <Card
          className={`${
            selectedThread
              ? 'fixed inset-x-0 bottom-[calc(56px+env(safe-area-inset-bottom))] top-12 z-30 flex'
              : 'hidden lg:flex'
          } min-h-0 flex-col overflow-hidden rounded-none md:static md:inset-auto md:min-h-[400px] md:rounded-[16px]`}
        >
          {!selectedThread ? (
            <CardContent className="flex-1 flex items-center justify-center text-glass-text-2 text-sm">
              Select a conversation
            </CardContent>
          ) : (
            <>
              <CardHeader className="space-y-2 border-b border-glass p-2.5 md:p-6 md:pb-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedThreadId(null)}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-glass-text-2 transition-colors hover:bg-glass-2 hover:text-glass-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50 lg:hidden"
                    aria-label="Back to conversations"
                  >
                    <ArrowLeft className="h-5 w-5" aria-hidden />
                  </button>
                  {selectedThread.kolProfile?.profileImageUrl ? (
                    <img
                      src={resolveMediaUrl(selectedThread.kolProfile.profileImageUrl)}
                      alt=""
                      className="h-10 w-10 shrink-0 rounded-full border border-glass object-cover"
                    />
                  ) : (
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent2/15 text-sm font-bold text-accentText">
                      {threadLabel(selectedThread).slice(0, 1).toUpperCase()}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <CardTitle className="truncate text-[15px] font-semibold">
                      {threadLabel(selectedThread)}
                    </CardTitle>
                    <p className="mt-0.5 truncate text-[11px] text-glass-text-3">
                      {selectedThread.waPhoneE164}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1.5">
                    {!orgReadOnly && (
                      <>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => handleAssign(false)}
                          className="h-9 w-9 p-0 md:w-auto md:px-3"
                          aria-label="Claim conversation"
                        >
                          <UserCheck size={15} className="md:mr-1" />
                          <span className="hidden md:inline">Claim</span>
                        </Button>
                        {selectedThread.assignedUserId === user?.id && (
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={handleRelease}
                            className="h-9 w-9 p-0 md:w-auto md:px-3"
                            aria-label="Release conversation"
                          >
                            <UserMinus size={15} className="md:mr-1" />
                            <span className="hidden md:inline">Release</span>
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="glass-scroll flex gap-2 overflow-x-auto md:flex-wrap">
                  {selectedThread.kolProfile?.id && (
                    <Link
                      to={`/agency/kols/${selectedThread.kolProfile.id}`}
                      className="min-h-9 shrink-0 rounded-full border border-accent2/30 bg-accent2/10 px-3 py-2 text-xs font-semibold text-accentText hover:bg-accent2/15"
                    >
                      View KOL profile
                    </Link>
                  )}
                  {selectedThread.job?.id && (
                    <Link
                      to={`/agency/campaigns/${selectedThread.job.id}`}
                      className="min-h-9 shrink-0 rounded-full border border-accent2/30 bg-accent2/10 px-3 py-2 text-xs font-semibold text-accentText hover:bg-accent2/15"
                    >
                      View campaign
                    </Link>
                  )}
                </div>
                {error ? (
                  <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-300">
                    <AlertCircle size={14} className="mt-0.5 shrink-0" />
                    {error}
                  </div>
                ) : null}
                {orgReadOnly ? (
                  <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
                    Read-only mode — you can read this conversation but cannot send messages.
                  </div>
                ) : null}
                {!windowStatus.freeTextAllowed && (
                  <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
                    24h window closed — use a Meta-approved template name to nudge the KOL.
                    {windowStatus.expiresAt && (
                      <span className="block text-glass-text-2">
                        Last window ended {formatTs(windowStatus.expiresAt)}
                      </span>
                    )}
                  </div>
                )}
              </CardHeader>

              <CardContent className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-4 md:px-6">
                {loadingMessages && <p className="text-sm text-glass-text-2">Loading messages…</p>}
                {messages.map((m, index) => {
                  const outbound = m.direction === 'OUTBOUND';
                  const day = messageDay(m.createdAt);
                  const previousDay = index > 0 ? messageDay(messages[index - 1].createdAt) : '';
                  return (
                    <div key={m.id} className="contents">
                      {day && day !== previousDay ? (
                        <div className="flex items-center gap-2 py-1 text-[10px] font-semibold uppercase tracking-[0.05em] text-glass-text-3">
                          <span className="h-px flex-1 bg-[rgb(var(--glass-border)/var(--glass-border-a))]" />
                          {day}
                          <span className="h-px flex-1 bg-[rgb(var(--glass-border)/var(--glass-border-a))]" />
                        </div>
                      ) : null}
                      <div className={`flex ${outbound ? 'justify-end' : 'justify-start'}`}>
                        <div
                          className={`max-w-[85%] rounded-2xl border px-3 py-2 text-sm ${
                            outbound
                              ? 'border-accent2/30 bg-accent2/20 text-glass-text'
                              : 'border-glass bg-glass-2 text-glass-text'
                          }`}
                      >
                          <p className="whitespace-pre-wrap break-words">{m.body || '—'}</p>
                          <p className="mt-1 text-[10px] text-glass-text-2">
                            {messageTime(m.createdAt)}
                            {outbound && m.sentByUser?.fullName
                              ? ` · ${m.sentByUser.fullName}`
                              : ''}
                            {m.deliveryStatus ? ` · ${m.deliveryStatus}` : ''}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </CardContent>

              {!orgReadOnly && (
                <div className="shrink-0 space-y-2 border-t border-glass bg-glass-1 p-2.5 backdrop-blur-xl md:p-3">
                  {quickReplies.length > 0 && windowStatus.freeTextAllowed && (
                    <div className="glass-scroll flex gap-2 overflow-x-auto pb-1">
                      {quickReplies.map((q) => (
                        <button
                          key={q.id}
                          type="button"
                          onClick={() => setComposer(q.body)}
                          className="min-h-9 shrink-0 rounded-full border border-glass px-3 py-2 text-xs hover:bg-glass-2"
                          title={q.body}
                        >
                          {q.title}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    {windowStatus.freeTextAllowed ? (
                      <Textarea
                        value={composer}
                        onChange={(e) => setComposer(e.target.value)}
                        placeholder="Type a reply…"
                        rows={1}
                        className="max-h-28 min-h-11 flex-1 resize-none text-sm"
                      />
                    ) : (
                      <Input
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                        placeholder="Meta template name"
                        className="h-11 min-w-0 flex-1 text-sm"
                      />
                    )}
                    <Button
                      type="button"
                      onClick={handleSend}
                      disabled={sending || composerDisabled}
                      className="h-11 w-11 shrink-0 p-0 md:w-auto md:px-4"
                      aria-label={sending ? 'Sending message' : 'Send message'}
                    >
                      <Send size={17} className="md:mr-1" />
                      <span className="hidden md:inline">{sending ? 'Sending…' : 'Send'}</span>
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      <Modal
        isOpen={newConversationOpen}
        onClose={() => {
          if (startingConversation) return;
          setNewConversationOpen(false);
          setNewConversationError('');
        }}
        title="New WhatsApp conversation"
        size="sm"
        mobilePresentation="sheet"
        footer={(
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary"
              className="h-11 flex-1"
              onClick={() => {
                setNewConversationOpen(false);
                setNewConversationError('');
              }}
              disabled={startingConversation}
            >
              Cancel
            </Button>
            <Button
              data-help-id="whatsapp.new_conversation_button"
              type="button"
              className="h-11 flex-1"
              onClick={() => void handleStartThread('sheet')}
              disabled={!newPhone.trim() || startingConversation}
            >
              {startingConversation ? 'Starting…' : 'Start chat'}
            </Button>
          </div>
        )}
      >
        <label htmlFor="new-whatsapp-phone" className="block text-xs font-semibold text-glass-text-2">
          Phone number
        </label>
        <Input
          id="new-whatsapp-phone"
          value={newPhone}
          onChange={(event) => {
            setNewPhone(event.target.value);
            if (newConversationError) setNewConversationError('');
          }}
          placeholder="+60…"
          className="mt-1.5 h-11"
          autoFocus
        />
        {newConversationError ? (
          <div
            className="mt-3 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300"
            role="alert"
          >
            <AlertCircle size={16} className="mt-0.5 shrink-0" aria-hidden />
            {newConversationError}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
